import { useEffect, useState } from "react";
import { api } from "../../shared/api/client";
import type { ReleaseDetail, ReleaseRollbackPlan } from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Input, Select, Textarea } from "../../shared/ui";
import { ConflictNotice } from "./ConflictNotice";
import { useReleaseMutation } from "./useReleaseMutation";

/** Three-state answers: an unanswered question is not a "no" (§39). */
const TRISTATE: [string, string][] = [
  ["", "Not recorded"],
  ["true", "Yes"],
  ["false", "No"],
];

/**
 * Writing the rollback plan (§39).
 *
 * <p>The trigger field is the one that matters most and is easiest to leave blank. Deciding
 * <em>whether</em> to roll back at two in the morning, with a director on the call, is not the
 * moment to work out what would justify it — so the question is asked here, in daylight, and the
 * gate refuses a plan that skipped it.
 */
export function RollbackPlanDrawer({
  release,
  plan,
  open,
  onClose,
  onSaved,
}: {
  release: ReleaseDetail;
  plan: ReleaseRollbackPlan;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [trigger, setTrigger] = useState("");
  const [maxRecovery, setMaxRecovery] = useState("");
  const [dbPossible, setDbPossible] = useState("");
  const [migrationReversible, setMigrationReversible] = useState("");
  const [previousArtifact, setPreviousArtifact] = useState("");
  const [backup, setBackup] = useState("");
  const [validationMethod, setValidationMethod] = useState("");
  const [notes, setNotes] = useState("");
  const [required, setRequired] = useState(true);
  const [exemption, setExemption] = useState("");
  const { run, error, conflict, saving, reset } = useReleaseMutation(release.id);

  useEffect(() => {
    if (!open) {
      return;
    }
    setTrigger(plan.triggerDescription ?? "");
    setMaxRecovery(plan.maxRecoveryMinutes ? String(plan.maxRecoveryMinutes) : "");
    setDbPossible(plan.databaseRollbackPossible == null
      ? "" : String(plan.databaseRollbackPossible));
    setMigrationReversible(plan.migrationReversible == null
      ? "" : String(plan.migrationReversible));
    setPreviousArtifact(plan.previousArtifactVersion ?? "");
    setBackup(plan.backupReference ?? "");
    setValidationMethod(plan.validationMethod ?? "");
    setNotes(plan.notes ?? "");
    setRequired(plan.required);
    setExemption(plan.exemptionReason ?? "");
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, plan]);

  const tri = (value: string) => (value === "" ? null : value === "true");
  const canSubmit = required || exemption.trim() !== "";

  const submit = () =>
    void run(
      async () => {
        if (required !== plan.required) {
          await api.put(`/api/v1/releases/${release.id}/rollback/required`, {
            required,
            reason: required ? null : exemption.trim(),
            lockVersion: plan.lockVersion,
          });
        }
        await api.patch(`/api/v1/releases/${release.id}/rollback`, {
          decisionOwnerId: plan.decisionOwner?.id ?? null,
          triggerDescription: trigger.trim() === "" ? null : trigger.trim(),
          maxRecoveryMinutes: maxRecovery === "" ? null : Number(maxRecovery),
          databaseRollbackPossible: tri(dbPossible),
          migrationReversible: tri(migrationReversible),
          previousArtifactVersion:
            previousArtifact.trim() === "" ? null : previousArtifact.trim(),
          previousConfigVersion: plan.previousConfigVersion,
          backupReference: backup.trim() === "" ? null : backup.trim(),
          validationMethod: validationMethod.trim() === "" ? null : validationMethod.trim(),
          notes: notes.trim() === "" ? null : notes.trim(),
          // Re-read: the required toggle above may already have moved the version on.
          lockVersion: required !== plan.required ? plan.lockVersion + 1 : plan.lockVersion,
        });
      },
      () => {
        onSaved();
        onClose();
      },
    );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Rollback plan"
      subtitle={`${release.releaseKey} — ${release.releaseName}`}
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit} loading={saving}>
            {saving ? "Saving…" : "Save plan"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {conflict ? <ConflictNotice onRefresh={onSaved} /> : error && (
          <InlineAlert tone="error">{error}</InlineAlert>
        )}

        <label className="flex items-start gap-2 text-sm text-ink-800">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={required}
            onChange={(event) => setRequired(event.target.checked)}
          />
          <span>
            This release needs a rollback plan
            <span className="block text-xs text-ink-500">
              Production normally does. Turning it off is a decision, and needs a reason.
            </span>
          </span>
        </label>

        {!required && (
          <Field label="Why no rollback plan" required>
            <Textarea
              rows={2}
              value={exemption}
              placeholder="Configuration-only change to DEV; nothing to restore."
              onChange={(event) => setExemption(event.target.value)}
            />
          </Field>
        )}

        {required && (
          <>
            <Field
              label="What would make somebody roll back"
              hint="Decided now, in daylight, rather than at two in the morning."
            >
              <Textarea
                rows={3}
                value={trigger}
                placeholder="Payment error rate above 2% for five minutes, or any data-integrity alert."
                onChange={(event) => setTrigger(event.target.value)}
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Maximum recovery (minutes)">
                <Input
                  type="number"
                  min={0}
                  value={maxRecovery}
                  onChange={(event) => setMaxRecovery(event.target.value)}
                />
              </Field>
              <Field label="Previous artifact">
                <Input
                  value={previousArtifact}
                  placeholder="2.0.35"
                  onChange={(event) => setPreviousArtifact(event.target.value)}
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Database rollback possible">
                <Select
                  value={dbPossible}
                  onChange={(event) => setDbPossible(event.target.value)}
                >
                  {TRISTATE.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Migration reversible"
                hint="Separate question — data written since may make it unacceptable anyway."
              >
                <Select
                  value={migrationReversible}
                  onChange={(event) => setMigrationReversible(event.target.value)}
                >
                  {TRISTATE.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>

            <Field label="Backup reference">
              <Input
                value={backup}
                placeholder="s3://backups/prod-2026-09-03"
                onChange={(event) => setBackup(event.target.value)}
              />
            </Field>

            <Field label="How to check the rollback worked">
              <Textarea
                rows={2}
                value={validationMethod}
                onChange={(event) => setValidationMethod(event.target.value)}
              />
            </Field>

            <Field label="Notes">
              <Textarea
                rows={2}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />
            </Field>
          </>
        )}
      </div>
    </Drawer>
  );
}
